import { Injectable } from '@angular/core';
import { State, Selector, StateContext, Action, createSelector } from '@ngxs/store';
import { ApiService } from '../services/api.service';
import { firstValueFrom } from 'rxjs';
import {
  AssignUploadToProject,
  DeleteRisicogrid,
  DeleteUpload,
  UpdateUpload,
  ListUploadsForAdmin,
  ListUploadsForOrganisation,
  SetSelectedUploadId,
  UploadsStateModel,
  GetUpload,
  EnsureUpload,
} from './models/uploads.state.model';
import { Upload } from '../interfaces/upload.interface';
import { produce } from 'immer';
import { PaginationMeta } from '../interfaces/pagination.interface';

const defaultUploadsState: UploadsStateModel = {
  uploads: {},
  selectedUploadId: null,
  pagination: null,
  uploadsCount: 0,
  loading: false,
  currentSort: null,
};

@State<UploadsStateModel>({
  name: 'uploads',
  defaults: defaultUploadsState,
})
@Injectable()
export class UploadsState {
  @Selector([UploadsState])
  static uploads(state: UploadsStateModel): Upload[] {
    return Object.values(state.uploads) || [];
  }

  @Selector([UploadsState])
  static uploadsCount(state: UploadsStateModel): number {
    return state.uploadsCount;
  }

  @Selector([UploadsState])
  static pagination(state: UploadsStateModel) {
    return state.pagination;
  }

  @Selector([UploadsState])
  static loading(state: UploadsStateModel) {
    return state.loading;
  }

  @Selector([UploadsState])
  static currentSort(state: UploadsStateModel) {
    return state.currentSort;
  }

  static uploadAlreadyExists(toBeUploadedFilename: string) {
    return createSelector([UploadsState.uploads], (state: Upload[]) => {
      const filenames = state.map((upload) => upload.originalFileName);
      return filenames.includes(toBeUploadedFilename);
    });
  }

  @Selector([UploadsState])
  static selectedUploadId(state: UploadsStateModel) {
    return state.selectedUploadId;
  }

  /**
   * Throws if no upload is selected, or if the selected upload isn't loaded in state.
   * Only call this where an upload is *guaranteed* to be selected and loaded
   * (e.g. anywhere under the `/klic/:uploadId` route, protected by `UploadSelectionResolver`).
   * If a component may render both with and without a selected upload (e.g. the `/veilig-graven/*`
   * pages, which are also reachable standalone), guard the call with
   * `route.snapshot.params['uploadId'] ? selectSignal(UploadsState.selectedUpload) : signal(null)`
   * instead of calling this unconditionally.
   */
  @Selector([UploadsState])
  static selectedUpload(state: UploadsStateModel): Upload {
    if (state.selectedUploadId === null) {
      throw new Error('NoUploadSelectedError');
    }

    const upload = state.uploads[state.selectedUploadId];

    if (!upload) {
      throw new Error(`Upload ${state.selectedUploadId} not found in state`);
    }

    return upload;
  }

  static numberOfUploadsForOrganisation(organisationId: string) {
    return createSelector([UploadsState.uploads], (uploads: Upload[]) => {
      return uploads.filter((upload) => upload.organisationId === organisationId).length;
    });
  }

  static getUploadById(uploadId: string) {
    return createSelector([UploadsState], (state: UploadsStateModel) => {
      return state.uploads[uploadId];
    });
  }

  constructor(private apiService: ApiService) {}

  @Action(ListUploadsForOrganisation)
  async listUploadsForOrganisation(ctx: StateContext<UploadsStateModel>, action: ListUploadsForOrganisation) {
    ctx.patchState({ loading: true });

    try {
      const paginatedResponse = await firstValueFrom(
        this.apiService.listUploadsForOrganisation({
          organisationId: action.payload.organisationId,
          params: action.payload.params,
        })
      );

      const uploadsMap: Record<string, Upload> = {};
      for (const upload of paginatedResponse.rows) {
        uploadsMap[upload.id] = upload;
      }

      const currentPage = action.payload.params.page || 1;
      const pageSize = action.payload.params.limit || 15;
      const paginationMeta: PaginationMeta = {
        currentPage: currentPage,
        totalCount: paginatedResponse.total,
        pageSize: pageSize,
        hasNext: currentPage * pageSize < paginatedResponse.total,
        hasPrev: currentPage > 1,
      };

      const currentSort =
        action.payload.params.sortBy && action.payload.params.sortDirection
          ? { field: action.payload.params.sortBy, direction: action.payload.params.sortDirection }
          : null;

      ctx.patchState({
        uploads: uploadsMap,
        pagination: paginationMeta,
        loading: false,
        currentSort,
      });
    } catch (error) {
      ctx.patchState({ loading: false });
      throw error;
    }
  }

  @Action(EnsureUpload)
  async ensureUpload(ctx: StateContext<UploadsStateModel>, action: EnsureUpload) {
    if (ctx.getState().uploads[action.payload.uploadId]) {
      return;
    }
    await firstValueFrom(ctx.dispatch(new GetUpload(action.payload)));
  }

  @Action(GetUpload)
  async getUpload(ctx: StateContext<UploadsStateModel>, action: GetUpload) {
    const upload = await firstValueFrom(
      this.apiService.getUpload({ organisationId: action.payload.organisationId, uploadId: action.payload.uploadId })
    );

    const state = produce(ctx.getState(), (draft) => {
      draft.uploads[action.payload.uploadId] = upload;
    });
    ctx.setState(state);
  }

  @Action(ListUploadsForAdmin)
  async listUploadsForAdmin(ctx: StateContext<UploadsStateModel>, action: ListUploadsForAdmin) {
    let params: any = {};
    if (action.payload.q !== undefined) {
      params.q = action.payload.q;
    }
    if (action.payload.status !== undefined) {
      params.status = action.payload.status;
    }
    if (action.payload.limit !== undefined) {
      params.limit = action.payload.limit;
    }
    if (action.payload.page !== undefined) {
      params.page = action.payload.page;
    }
    const response = await firstValueFrom(this.apiService.listUploadsForAdmin(params));

    const uploadsMap = Object.fromEntries(response.data.map((upload: Upload) => [upload.id, upload]));

    ctx.patchState({
      uploads: uploadsMap,
      uploadsCount: response.count,
    });
  }

  @Action(DeleteUpload)
  async deleteUpload(ctx: StateContext<UploadsStateModel>, action: DeleteUpload) {
    await firstValueFrom(this.apiService.deleteUpload(action.payload.uploadId));

    const state = produce(ctx.getState(), (draft) => {
      delete draft.uploads[action.payload.uploadId];
    });
    ctx.setState(state);
  }

  @Action(DeleteRisicogrid)
  async deleteRisicogrid(ctx: StateContext<UploadsStateModel>, action: DeleteRisicogrid) {
    const upload = (await firstValueFrom(this.apiService.deleteRiskGrid(action.payload.uploadId))) as any;

    const state = produce(ctx.getState(), (draft) => {
      draft.uploads[action.payload.uploadId] = upload;
    });
    ctx.setState(state);
  }

  @Action(AssignUploadToProject)
  async assignUploadToProject(ctx: StateContext<UploadsStateModel>, action: AssignUploadToProject) {
    const newUpload = await firstValueFrom(this.apiService.assignUploadToProject(action.payload));
    const state = produce(ctx.getState(), (draft) => {
      // if-statement needed for assigning/ removing inactive uploads to project. If latest filter does not match the status of assigned upload it's not in the state...
      if (draft.uploads[action.payload.uploadId]) {
        draft.uploads[action.payload.uploadId] = newUpload;
      }
    });
    ctx.setState(state);
  }

  @Action(SetSelectedUploadId)
  async setSelectedUploadId(ctx: StateContext<UploadsStateModel>, action: SetSelectedUploadId) {
    ctx.patchState({ selectedUploadId: action.payload.uploadId });
  }

  @Action(UpdateUpload)
  async editUpload(ctx: StateContext<UploadsStateModel>, action: UpdateUpload) {
    const updatedUpload = await firstValueFrom(
      this.apiService.updateUpload({
        uploadId: action.payload.uploadId,
        uploadAttributes: action.payload.uploadAttributes,
      })
    );
    const state = produce(ctx.getState(), (draft) => {
      draft.uploads[action.payload.uploadId] = updatedUpload!;
    });
    ctx.setState(state);
  }
}
